/**
 * Block Editor Reducer
 * 管理 blocks 状态、undo/redo 历史
 */

import { useReducer, useCallback } from 'react';
import type { NoteBlock } from '../../../types';
import type { BlockEditorState, BlockAction } from './types';

const MAX_HISTORY = 50;

function generateId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/** 将当前 blocks + 光标信息推入 history，清空 future */
function pushHistory(state: BlockEditorState): BlockEditorState {
  const snapshot = {
    blocks: state.blocks,
    activeBlockId: state.activeBlockId,
    cursorOffset: state.cursorOffset,
  };
  const history = [...state.history, snapshot];
  if (history.length > MAX_HISTORY) history.shift();
  return { ...state, history, future: [] };
}

function blockReducer(state: BlockEditorState, action: BlockAction): BlockEditorState {
  switch (action.type) {
    case 'INIT':
      return {
        ...state,
        blocks: action.blocks,
        history: [],
        future: [],
        activeBlockId: null,
        cursorOffset: null,
        selectedBlockIds: [],
      };

    case 'MOVE': {
      const s = pushHistory(state);
      const blocks = [...s.blocks];
      const [moved] = blocks.splice(action.sourceIndex, 1);
      blocks.splice(action.destIndex, 0, moved);
      return { ...s, blocks };
    }

    case 'MOVE_SELECTED': {
      if (action.selectedIds.length === 0) return state;
      const s = pushHistory(state);
      const idSet = new Set(action.selectedIds);
      // 提取选中的 blocks（保持原始顺序）
      const selectedBlocks = s.blocks.filter(b => idSet.has(b.id));
      const remaining = s.blocks.filter(b => !idSet.has(b.id));
      // 计算插入位置：destIndex 是基于原始 blocks 的索引
      // 需要转换为 remaining 数组中的位置
      let insertAt = action.destIndex;
      // 计算 destIndex 之前有多少个被选中的 block
      let selectedBefore = 0;
      for (let i = 0; i < action.destIndex && i < s.blocks.length; i++) {
        if (idSet.has(s.blocks[i].id)) selectedBefore++;
      }
      insertAt = Math.min(action.destIndex - selectedBefore, remaining.length);
      remaining.splice(insertAt, 0, ...selectedBlocks);
      return { ...s, blocks: remaining };
    }

    case 'DELETE': {
      if (action.ids.length === 0) return state;
      const s = pushHistory(state);
      const idSet = new Set(action.ids);
      const blocks = s.blocks.filter(b => !idSet.has(b.id));
      // 如果删光了，保留一个空 block
      if (blocks.length === 0) {
        blocks.push({ id: generateId(), content: '', level: 1 });
      }
      // 聚焦到被删 block 的前一个
      const deletedIdx = s.blocks.findIndex(b => idSet.has(b.id));
      const focusIdx = Math.max(0, deletedIdx - 1);
      const focusBlock = blocks[Math.min(focusIdx, blocks.length - 1)];
      return {
        ...s,
        blocks,
        activeBlockId: focusBlock?.id ?? null,
        cursorOffset: focusBlock ? focusBlock.content.length : null,
        selectedBlockIds: [],
      };
    }

    case 'REPLACE_SELECTED': {
      if (action.ids.length === 0) return state;
      const s = pushHistory(state);
      const idSet = new Set(action.ids);
      // 找到第一个选中 block 的位置
      const firstIdx = s.blocks.findIndex(b => idSet.has(b.id));
      // 创建替换 block
      const newBlock: NoteBlock = {
        id: generateId(),
        content: action.content,
        level: action.level,
      };
      // 过滤掉选中的 block，在原位置插入新 block
      const blocks: NoteBlock[] = [];
      let inserted = false;
      for (let i = 0; i < s.blocks.length; i++) {
        if (idSet.has(s.blocks[i].id)) {
          if (!inserted) {
            blocks.push(newBlock);
            inserted = true;
          }
          // 跳过被删除的 block
        } else {
          blocks.push(s.blocks[i]);
        }
      }
      if (!inserted) {
        blocks.push(newBlock);
      }
      return {
        ...s,
        blocks,
        activeBlockId: newBlock.id,
        cursorOffset: action.content.length,
        selectedBlockIds: [],
      };
    }

    case 'ADD': {
      const s = pushHistory(state);
      const newBlock: NoteBlock = {
        id: generateId(),
        content: action.content,
        level: action.level,
      };
      // 如果是从某个 block 拆分出来的，继承源 block 的额外字段（linkedTranscriptId 等）
      if (action.afterId !== null) {
        const sourceBlock = s.blocks.find(b => b.id === action.afterId);
        if (sourceBlock) {
          if (sourceBlock.linkedTranscriptId) newBlock.linkedTranscriptId = sourceBlock.linkedTranscriptId;
          if (sourceBlock.originalParagraphIndex !== undefined) newBlock.originalParagraphIndex = sourceBlock.originalParagraphIndex;
          if (sourceBlock.type) newBlock.type = sourceBlock.type;
        }
      }
      const blocks = [...s.blocks];
      if (action.afterId === null) {
        blocks.push(newBlock);
      } else {
        const idx = blocks.findIndex(b => b.id === action.afterId);
        blocks.splice(idx + 1, 0, newBlock);
      }
      return {
        ...s,
        blocks,
        activeBlockId: action.focusNew !== false ? newBlock.id : s.activeBlockId,
        cursorOffset: action.focusNew !== false ? 0 : s.cursorOffset,
      };
    }

    case 'UPDATE_CONTENT': {
      const blockIdx = state.blocks.findIndex(b => b.id === action.id);
      if (blockIdx === -1) return state;
      const oldContent = state.blocks[blockIdx].content;
      if (oldContent === action.content) return state;
      // 计算编辑前的光标位置：当前光标位置减去内容长度变化
      // 这样 undo 恢复旧内容时，光标在编辑发生的位置
      let prevCursorOffset: number | null = null;
      if (action.cursorOffset !== undefined) {
        const delta = action.content.length - oldContent.length;
        prevCursorOffset = Math.max(0, action.cursorOffset - delta);
      }
      const stateWithCursor = {
        ...state,
        activeBlockId: action.id,
        cursorOffset: prevCursorOffset,
      };
      const s = pushHistory(stateWithCursor);
      const blocks = [...s.blocks];
      blocks[blockIdx] = { ...blocks[blockIdx], content: action.content };
      return { ...s, blocks, cursorOffset: action.cursorOffset ?? null };
    }

    case 'UPDATE_LEVEL': {
      const blockIdx = state.blocks.findIndex(b => b.id === action.id);
      if (blockIdx === -1) return state;
      const clamped = Math.max(1, Math.min(5, action.level));
      if (state.blocks[blockIdx].level === clamped) return state;
      const s = pushHistory(state);
      const blocks = [...s.blocks];
      blocks[blockIdx] = { ...blocks[blockIdx], level: clamped };
      return { ...s, blocks };
    }

    case 'MERGE': {
      const s = pushHistory(state);
      const blocks = [...s.blocks];
      const destIdx = blocks.findIndex(b => b.id === action.destId);
      const srcIdx = blocks.findIndex(b => b.id === action.srcId);
      if (destIdx === -1 || srcIdx === -1) return state;
      // 更新 dest 的 content
      blocks[destIdx] = { ...blocks[destIdx], content: action.mergedContent };
      // 删除 src
      blocks.splice(srcIdx, 1);
      return {
        ...s,
        blocks,
        activeBlockId: action.destId,
        cursorOffset: action.cursorOffset,
      };
    }

    case 'UNDO': {
      if (state.history.length === 0) return state;
      const history = [...state.history];
      const prev = history.pop()!;
      const currentSnapshot = {
        blocks: state.blocks,
        activeBlockId: state.activeBlockId,
        cursorOffset: state.cursorOffset,
      };
      return {
        ...state,
        blocks: prev.blocks,
        history,
        future: [currentSnapshot, ...state.future],
        activeBlockId: prev.activeBlockId,
        cursorOffset: prev.cursorOffset,
        selectedBlockIds: [],
      };
    }

    case 'REDO': {
      if (state.future.length === 0) return state;
      const future = [...state.future];
      const next = future.shift()!;
      const currentSnapshot = {
        blocks: state.blocks,
        activeBlockId: state.activeBlockId,
        cursorOffset: state.cursorOffset,
      };
      return {
        ...state,
        blocks: next.blocks,
        history: [...state.history, currentSnapshot],
        future,
        activeBlockId: next.activeBlockId,
        cursorOffset: next.cursorOffset,
        selectedBlockIds: [],
      };
    }

    case 'SET_ACTIVE':
      return {
        ...state,
        activeBlockId: action.id,
        cursorOffset: action.cursorOffset ?? null,
      };

    case 'SET_SELECTION':
      return {
        ...state,
        selectedBlockIds: action.ids,
      };

    case 'TOGGLE_SELECTION': {
      const isSelected = state.selectedBlockIds.includes(action.id);
      const newSelection = isSelected
        ? state.selectedBlockIds.filter(id => id !== action.id)
        : [...state.selectedBlockIds, action.id];
      return {
        ...state,
        selectedBlockIds: newSelection,
        activeBlockId: action.id,
        cursorOffset: null,
      };
    }

    case 'RANGE_SELECTION': {
      // Shift+Click: 从 activeBlockId 到 action.id 之间的所有 blocks
      const anchorId = state.activeBlockId;
      if (!anchorId) {
        return { ...state, selectedBlockIds: [action.id], activeBlockId: action.id };
      }
      const anchorIdx = state.blocks.findIndex(b => b.id === anchorId);
      const targetIdx = state.blocks.findIndex(b => b.id === action.id);
      if (anchorIdx === -1 || targetIdx === -1) return state;
      const start = Math.min(anchorIdx, targetIdx);
      const end = Math.max(anchorIdx, targetIdx);
      const rangeIds = state.blocks.slice(start, end + 1).map(b => b.id);
      return {
        ...state,
        selectedBlockIds: rangeIds,
      };
    }

    default:
      return state;
  }
}

export function useBlockReducer(initialBlocks: NoteBlock[]) {
  const initialState: BlockEditorState = {
    blocks: initialBlocks,
    history: [],
    future: [],
    activeBlockId: null,
    cursorOffset: null,
    selectedBlockIds: [],
  };

  return useReducer(blockReducer, initialState);
}

/**
 * 设置光标位置到 contentEditable 元素
 */
export function setCursorPosition(el: HTMLElement, offset: number) {
  const range = document.createRange();
  const sel = window.getSelection();
  if (!sel) return;

  const textNode = el.firstChild;
  if (textNode && textNode.nodeType === Node.TEXT_NODE) {
    const safeOffset = Math.min(offset, textNode.textContent?.length ?? 0);
    range.setStart(textNode, safeOffset);
    range.collapse(true);
  } else if (offset === 0) {
    range.setStart(el, 0);
    range.collapse(true);
  } else {
    // 空元素，光标放在开头
    range.selectNodeContents(el);
    range.collapse(false);
  }

  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * 获取当前光标在 contentEditable 中的偏移量
 */
export function getCursorOffset(el: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;
  const range = sel.getRangeAt(0);
  const preRange = range.cloneRange();
  preRange.selectNodeContents(el);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString().length;
}
